import { Icon, IconButton, Input } from '@chakra-ui/react'
import debounce from 'lodash/debounce'
import type { FC } from 'react'
import { useCallback } from 'react'
import { useEffect, useRef, memo } from 'react'
import { FiXCircle, FiSearch } from 'react-icons/fi'

import type { InputGroupProps } from '@app/components/ui/input-group'
import { InputGroup } from '@app/components/ui/input-group'
import { useListFilterContext } from '@app/pages/shared/listFilterContext'

interface Props extends Omit<InputGroupProps, 'children'> {
  field: string
  placeholder?: string
}

const FieldSearchInput: FC<Props> = ({ ...rest }) => {
  const { listFilter: defaultValue, setListFilter } = useListFilterContext()
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (ref?.current) {
      ref.current.value = defaultValue
    }
  }, [defaultValue])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChange = useCallback(
    debounce(async ({ target: { value } }) => {
      setListFilter(value)
    }, 150),
    [setListFilter]
  )

  const clearSearch = useCallback(async () => {
    if (ref?.current) {
      ref.current.value = ''
    }

    setListFilter(null)
  }, [setListFilter])

  const { placeholder, ...inputGroupProps } = rest

  return (
    <InputGroup
      {...inputGroupProps}
      startElement={
        <Icon>
          <FiSearch />
        </Icon>
      }
      endElement={
        <IconButton aria-label="Clear search" onClick={clearSearch} variant="plain">
          <Icon color="gray">
            <FiXCircle />
          </Icon>
        </IconButton>
      }
    >
      <Input
        ref={ref}
        autoFocus
        data-cy="field-search-input"
        defaultValue={defaultValue}
        onChange={onChange}
        placeholder={placeholder}
      />
    </InputGroup>
  )
}

export default memo(FieldSearchInput)
